import {
  endOfDay,
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  min,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subQuarters,
  subWeeks,
  subYears,
} from "date-fns";

export type PresetId =
  | "this_week"
  | "this_month"
  | "this_quarter"
  | "this_year"
  | "last_week"
  | "last_month"
  | "last_quarter"
  | "last_year"
  | "last_30d"
  | "last_90d"
  | "last_365d"
  | "all_time"
  | "custom";

export interface PresetDateRange {
  from: Date;
  to: Date;
}

export interface PresetOption {
  id: PresetId;
  label: string;
}

const WEEK_OPTS = { weekStartsOn: 1 } as const;

export const PRESET_GROUPS: PresetOption[][] = [
  [
    { id: "this_week", label: "This week" },
    { id: "this_month", label: "This month" },
    { id: "this_quarter", label: "This quarter" },
    { id: "this_year", label: "This year" },
  ],
  [
    { id: "last_week", label: "Last week" },
    { id: "last_month", label: "Last month" },
    { id: "last_quarter", label: "Last quarter" },
    { id: "last_year", label: "Last year" },
  ],
  [
    { id: "last_30d", label: "Last 30 days" },
    { id: "last_90d", label: "Last 90 days" },
    { id: "last_365d", label: "Last 365 days" },
    { id: "all_time", label: "All time" },
  ],
];

export function parseAnalysisDate(isoDate: string): Date {
  const [year, month, day] = isoDate.split("-").map(Number);
  return new Date(year, month - 1, day);
}

export function getLatestDataDate(
  now: Date = new Date(),
  latestAnalysisDate?: Date | null
): Date {
  if (latestAnalysisDate) {
    return endOfDay(startOfDay(latestAnalysisDate));
  }
  return endOfDay(subDays(startOfDay(now), 1));
}

function capIncompletePeriodEnd(
  periodStart: Date,
  periodEnd: Date,
  now: Date,
  latestAnalysisDate?: Date | null
): Date {
  const latestData = getLatestDataDate(now, latestAnalysisDate);
  const capped = min([periodEnd, latestData]);
  if (capped < periodStart) {
    return periodStart;
  }
  return capped;
}

export function computePreviousPeriodRange(
  from: Date,
  to: Date
): PresetDateRange {
  const start = startOfDay(from);
  const end = endOfDay(to);
  const periodMs = end.getTime() - start.getTime();
  const previousEnd = endOfDay(subDays(start, 1));
  const previousStart = startOfDay(new Date(previousEnd.getTime() - periodMs));
  return { from: previousStart, to: previousEnd };
}

export function getPresetRange(
  id: PresetId,
  now: Date = new Date(),
  allTimeStart?: Date,
  latestAnalysisDate?: Date | null
): PresetDateRange {
  const latestData = getLatestDataDate(now, latestAnalysisDate);

  switch (id) {
    case "this_week": {
      const from = startOfWeek(now, WEEK_OPTS);
      return {
        from,
        to: capIncompletePeriodEnd(
          from,
          endOfWeek(now, WEEK_OPTS),
          now,
          latestAnalysisDate
        ),
      };
    }
    case "this_month": {
      const from = startOfMonth(now);
      return {
        from,
        to: capIncompletePeriodEnd(
          from,
          endOfMonth(now),
          now,
          latestAnalysisDate
        ),
      };
    }
    case "this_quarter": {
      const from = startOfQuarter(now);
      return {
        from,
        to: capIncompletePeriodEnd(
          from,
          endOfQuarter(now),
          now,
          latestAnalysisDate
        ),
      };
    }
    case "this_year": {
      const from = startOfYear(now);
      return {
        from,
        to: capIncompletePeriodEnd(
          from,
          endOfYear(now),
          now,
          latestAnalysisDate
        ),
      };
    }
    case "last_week": {
      const ref = subWeeks(now, 1);
      return {
        from: startOfWeek(ref, WEEK_OPTS),
        to: endOfWeek(ref, WEEK_OPTS),
      };
    }
    case "last_month": {
      const ref = subMonths(now, 1);
      return { from: startOfMonth(ref), to: endOfMonth(ref) };
    }
    case "last_quarter": {
      const ref = subQuarters(now, 1);
      return { from: startOfQuarter(ref), to: endOfQuarter(ref) };
    }
    case "last_year": {
      const ref = subYears(now, 1);
      return { from: startOfYear(ref), to: endOfYear(ref) };
    }
    case "last_30d":
      return {
        from: startOfDay(subDays(latestData, 29)),
        to: latestData,
      };
    case "last_90d":
      return {
        from: startOfDay(subDays(latestData, 89)),
        to: latestData,
      };
    case "last_365d":
      return {
        from: startOfDay(subDays(latestData, 364)),
        to: latestData,
      };
    case "all_time":
      return {
        from: startOfDay(allTimeStart ?? subYears(now, 2)),
        to: latestData,
      };
    case "custom":
    default:
      return {
        from: startOfDay(subDays(latestData, 29)),
        to: latestData,
      };
  }
}

function shiftRangeToPreviousPeriod(
  range: PresetDateRange,
  id: PresetId
): PresetDateRange {
  switch (id) {
    case "this_week":
      return {
        from: subWeeks(range.from, 1),
        to: subWeeks(range.to, 1),
      };
    case "this_month":
      return {
        from: subMonths(range.from, 1),
        to: subMonths(range.to, 1),
      };
    case "this_quarter":
      return {
        from: subQuarters(range.from, 1),
        to: subQuarters(range.to, 1),
      };
    case "this_year":
      return {
        from: subYears(range.from, 1),
        to: subYears(range.to, 1),
      };
    default:
      return range;
  }
}

export function getPreviousTemplateRange(
  id: PresetId,
  now: Date = new Date(),
  allTimeStart?: Date,
  latestAnalysisDate?: Date | null
): PresetDateRange {
  if (
    id === "this_week" ||
    id === "this_month" ||
    id === "this_quarter" ||
    id === "this_year"
  ) {
    const range = getPresetRange(id, now, allTimeStart, latestAnalysisDate);
    return shiftRangeToPreviousPeriod(range, id);
  }

  switch (id) {
    case "last_week": {
      const ref = subWeeks(now, 2);
      return {
        from: startOfWeek(ref, WEEK_OPTS),
        to: endOfWeek(ref, WEEK_OPTS),
      };
    }
    case "last_month": {
      const ref = subMonths(now, 2);
      return { from: startOfMonth(ref), to: endOfMonth(ref) };
    }
    case "last_quarter": {
      const ref = subQuarters(now, 2);
      return { from: startOfQuarter(ref), to: endOfQuarter(ref) };
    }
    case "last_year": {
      const ref = subYears(now, 2);
      return { from: startOfYear(ref), to: endOfYear(ref) };
    }
    default: {
      const range = getPresetRange(id, now, allTimeStart, latestAnalysisDate);
      return computePreviousPeriodRange(range.from, range.to);
    }
  }
}

export function isPresetId(value: string): value is PresetId {
  return (
    value === "custom" ||
    PRESET_GROUPS.some((group) => group.some((preset) => preset.id === value))
  );
}
